<template>
  <!-- Um valor = um elemento raiz. Só um ramo desta cadeia renderiza por vez. -->

  <!-- Relacionamento: o nome com a RÉGUA da instância à esquerda, e não uma caixa em volta.

       A régua é a mesma marca que o cabeçalho da ficha e a linha da listagem usam, na escala
       daqui — uma marca só para "isto é um item", em qualquer tamanho. E, não sendo texto, a
       cor deixa de disputar legibilidade: o nome volta à cor de leitura, sem escurecimento.

       Vários valores numa célula viram lista em vez de sacola de pílulas, e cada um continua
       delimitado pelo próprio traço — que é por que o separador `|` segue dispensável.

       No card é um `span`: ali quem abre a instância é o card inteiro, e um link dentro de
       link prometeria um clique que não existe. Nas demais telas é um `<a>` de verdade, com
       href, para ctrl+clique abrir a página inteira; o clique simples sobe no evento `link`,
       e quem decide entre modal e rota é o host. -->
  <span v-if="kind === 'rel' && variant === 'card'" class="pv-rel" :title="text">
    <ontos-color-accent v-if="accent" vertical :color="relColor" class="pv-rel-accent"/>
    <span class="pv-rel-text">{{ text }}</span>
  </span>

  <a v-else-if="kind === 'rel'" class="pv-rel pv-rel-link" :href="relHref" :title="text"
     @click="$emit('link', {event: $event, context: relContext})">
    <ontos-color-accent v-if="accent" vertical :color="relColor" class="pv-rel-accent"/>
    <span class="pv-rel-text">{{ text }}</span>
  </a>

  <!-- Seleção: o fundo tingido atrás do nome, igual em toda variante.

       A forma diz a natureza do valor: fundo tingido é opção de domínio, bolinha é situação de
       fluxo, régua é item de coleção. Por um tempo o card trocava o desenho pela bolinha para
       economizar altura, e o preço era a opção aparecer ali com a estética da situação, que é
       outra coisa. A altura deixou de ser um problema quando o próprio desenho passou a ser
       rente ao texto — e por isso ele vale em qualquer lugar, sem variante.

       Este não leva a lugar nenhum, então não tem href nem contorno de link. Opção que não se
       acha cai no v-else, e o texto é vazio.

       O desenho mora no ontos-colored-option porque o mesmo par serve à situação e à raia do
       Kanban, que não passam por aqui. -->
  <ontos-colored-option v-else-if="kind === 'choice' && option" :option="option"/>

  <!-- Usuário: a cara e o nome, sem o e-mail, que o modo de uso nem recebe (decisão do usuário).
       Menor na célula e no card.

       Quem não se pode escolher hoje (saiu do ambiente) fica no item, com nome, mas esmaecido e
       dito no título (decisão do usuário). Conta que não se acha cai no v-else, vazia. -->
  <ontos-person-line v-else-if="kind === 'user' && person" class="pv-person"
                     :class="{'pv-person--unassignable': !person.assignable}"
                     :title="person.assignable ? undefined : $tt('person-unassignable')"
                     :user="person.user" :name="person.name" :photo="person.photo"
                     :dense="variant !== 'detail'"/>

  <span v-else-if="kind === 'date'">{{ text }}</span>

  <!-- Sinal e palavra, em verde ou vermelho — ver `booleanFace`, que o campo do formulário
       também usa. -->
  <span v-else-if="kind === 'boolean'" class="pv-boolean" :class="`text-${face.color}`">
    <v-icon size="small" :color="face.color">{{ face.icon }}</v-icon>{{ face.label }}
  </span>

  <!-- Card do Kanban: caixa limitada, e não v-img. O VImg injeta
       width: <naturalWidth>px no conteúdo dele, ou seja a largura do ARQUIVO e não
       a exibida — uma imagem de 1600px virava a largura mínima da coluna e esticava
       a swimlane inteira, mesmo aparecendo pequena na tela.

       Não é clicável de propósito: o card inteiro já é link e alvo de arrasto. -->
  <div v-else-if="kind === 'image' && variant === 'card' && value" class="pv-image pv-card"
       :title="failed ? $tt('image.missing') : property.name">
    <img v-if="!failed" :src="value" :alt="property.name" loading="lazy" decoding="async"
         @error="failed = true">
    <v-icon v-else color="grey" size="small">mdi-image-broken-variant</v-icon>
  </div>

  <!-- Imagem nas telas: caixa de tamanho fixo, imagem inteira dentro, clique amplia.
       Antes isto usava .expandable-cell, que mostrava um pedaço e esticava no hover
       empurrando as linhas de baixo.

       <img> nativo e não v-img: o v-img do Vuetify 2 não tem slot de erro e o onError
       dele não desliga o isLoading, então imagem quebrada fica com spinner eterno.
       De quebra, loading=lazy dispensa o IntersectionObserver que ele criaria por
       instância — a listagem renderiza a página inteira no DOM.

       Quem abre a ampliação é o host: um estado guardado aqui ficaria preso à
       posição da célula, não ao dado. Mesma doutrina do evento `link`. -->
  <button v-else-if="kind === 'image' && value" type="button" class="pv-image" :class="variantClass"
          :disabled="failed" :title="failed ? $tt('image.missing') : property.name"
          @click.stop="$emit('preview', {src: value, title: property.name})">
    <img v-if="!failed" :src="value" :alt="property.name" loading="lazy" decoding="async"
         @error="failed = true">
    <v-icon v-else color="grey" :size="variant === 'cell' ? 'small' : undefined">mdi-image-broken-variant</v-icon>
    <span v-if="!failed" class="pv-image-zoom"><v-icon size="small" color="white">mdi-magnify-plus-outline</v-icon></span>
  </button>

  <!-- Áudio no card: só indicador, não toca. Além de não competir com o clique e o
       arrasto do card, há uma razão do próprio HTML: remover um elemento de mídia
       do documento pausa a reprodução, e o sortable move o nó ao arrastar — o som
       cortaria no meio, sem contorno. -->
  <span v-else-if="kind === 'audio' && variant === 'card' && value" class="pv-audio-mark"
        :title="property.name">
    <v-icon size="x-small" color="grey-darken-1">mdi-music-note</v-icon>
  </span>

  <!-- Nas telas: controle nativo onde há espaço, botão só na célula. O @click.stop
       do botão é o que impede a linha de abrir a instância junto. -->
  <ontos-audio-player v-else-if="kind === 'audio' && value"
                      :src="value" :label="property.name" :compact="variant === 'cell'"/>

  <!-- Markdown: num card vira excerto de uma linha; renderizar o editor ali seria
       absurdo — uma instância de ProseMirror por card, e um título ou uma imagem
       estourando a altura. As marcas que sobrevivem são só as que não mexem no
       tamanho nem na família da fonte; ver markdownSegments. -->
  <!-- O markdown como se mostra (`markdown`): as referências do app em link e o `{{campo}}` no valor —
       ver `markdownMentions` -->
  <ontos-markdown-excerpt v-else-if="kind === 'markdown' && variant === 'card'" :value="markdown"/>
  <!-- Na ficha, o de uma linha é um campo como os outros: linearizado, inteiro, sem borda (pedido do
       usuário). -->
  <ontos-markdown-excerpt v-else-if="kind === 'markdown' && variant === 'detail' && isSingleLineMarkdown(markdown)"
                          :value="markdown" full/>
  <!-- Na grade, e na ficha o de mais linhas: o resumo em até duas linhas, com o ⤢ que abre o texto
       desenhado por cima (ver MarkdownExpandable). Na ficha o texto inteiro tem também a aba dele (ver
       instanceSections). Era o markdown desenhado numa caixa que crescia ao passar o mouse: a linha
       mudava de altura sob o cursor e cobria as de baixo. -->
  <ontos-markdown-expandable v-else-if="kind === 'markdown'" :value="markdown" class="pv-markdown-cell"/>

  <!-- URL só vira link se urlHref aceitar; senão cai no texto puro do v-else -->
  <a v-else-if="kind === 'url' && href"
     :href="href"
     :title="text"
     target="_blank"
     rel="noopener noreferrer"
     class="url-value no-drag"
     :class="{'url-value--cell': variant === 'cell'}"
     @click.stop>{{ text }}</a>

  <span v-else>{{ text }}</span>
</template>

<script lang="ts">
import {Component, Vue, Prop, Inject, toNative} from 'vue-facing-decorator'
import PersonLine from "@/components/shared/PersonLine.vue";
import {WORKSPACE, WORKSPACE_PEOPLE} from "@/components/shared/WorkspaceRouteComponent";
import {EnhancedWorkpace} from "@/types/Workspace";
import {shownMarkdown} from "@/utils/markdownMentions";
import {WorkspacePerson} from "@/types/Person";
import MarkdownExpandable from '@/components/shared/markdown/MarkdownExpandable.vue'
import IdeaProperty from "@/types/IdeaProperty";
import {InstanceContext} from "@/types/InstanceContext";
import {displayKind, valueText, ValueKind} from "@/utils/propertyDisplay";
import MarkdownExcerpt from '@/components/shared/markdown/MarkdownExcerpt.vue'
import {isSingleLineMarkdown} from '@/utils/markdownExcerpt'
import {resolveInstanceHref} from "@/utils/instanceLink";
import utils from "@/utils/utils";
import AudioPlayer from "@/components/shared/AudioPlayer.vue";
import ColoredOptionChip from "@/components/shared/ColoredOptionChip.vue";
import ColorAccent from "@/components/shared/ColorAccent.vue";
import Domain, {DomainOption} from "@/types/Domain";
import {booleanFace, choiceOption, instanceColorOf, personOf} from "@/utils/propertyDisplay";

/**
 * Desenha UM valor de propriedade. Era a mesma cadeia de v-if repetida em
 * ListInstances, InstanceView e no card do Kanban; o que muda entre elas virou a
 * prop `variant`. O que é decisão de dado (qual campo ler, como formatar) mora em
 * utils/propertyDisplay.
 *
 * Sobre navegação: o href é montado aqui para que ctrl+clique e botão do meio
 * abram a página inteira, mas o clique simples sobe como evento `link` — quem
 * decide entre modal e rota é o host, via onInstanceLink.
 */
type Variant = 'cell' | 'detail' | 'card'



@Component({
  emits: ['link', 'preview'],
  components: {
    'ontos-markdown-expandable': MarkdownExpandable,
    'ontos-markdown-excerpt': MarkdownExcerpt,
    'ontos-audio-player': AudioPlayer,
    'ontos-colored-option': ColoredOptionChip,
    'ontos-color-accent': ColorAccent,
    'ontos-person-line': PersonLine
  }
})
class PropertyValue extends Vue {
  @Prop({type: Object, required: true}) readonly property!: IdeaProperty
  @Prop({required: true}) readonly value!: any
  @Prop({type: Object, required: true}) readonly context!: InstanceContext
  //O cast é o jeito de dar ao prop um tipo mais estreito que String
  @Prop({type: String as () => Variant, default: 'detail'}) readonly variant!: Variant
  /**
   * Desenhar a régua do item?
   *
   * `false` para quem já a desenha por fora e maior — o cabeçalho de uma seção de
   * relacionamento marca as duas linhas com uma régua só, e duas marcas para o mesmo item
   * seriam uma a mais.
   */
  @Prop({type: Boolean, default: true}) readonly accent!: boolean
  /** Os domínios do ambiente, para achar nome e cor de um valor de seleção. */
  @Prop({type: Array, default: () => []}) readonly choiceDomains!: Domain[]
  /** As pessoas do ambiente, para achar a de um valor de usuário — ver `WORKSPACE_PEOPLE`. */
  @Inject({from: WORKSPACE_PEOPLE, default: () => []}) readonly people!: WorkspacePerson[]
  /** O ambiente, para as referências do markdown — ver `markdownMentions`. */
  @Inject({from: WORKSPACE, default: null}) readonly workspace!: EnhancedWorkpace | null
  /**
   * O item dono do valor: no markdown, os nomes de agora das referências (`_mentions`) e os valores do
   * `{{campo}}`. Sem ele, as referências saem com o nome de reserva, e o `{{campo}}` vazio.
   */
  @Prop({type: Object, default: undefined}) readonly item?: Record<string, any>

  //exposta ao template: função de módulo não é enxergada de lá
  isSingleLineMarkdown = isSingleLineMarkdown

  /** O markdown como se mostra — ver `shownMarkdown`. */
  get markdown(): string {
    return shownMarkdown(this.value, this.workspace, this.item, this.context.idea)
  }

  /** Imagem que não carregou. A caixa tem tamanho fixo, então trocar pelo ícone
   *  não move nada em volta. */
  failed = false

  get kind(): ValueKind {
    return displayKind(this.property)
  }

  get text(): string {
    return valueText(this.property, this.value, {domains: this.choiceDomains, people: this.people})
  }

  get option(): DomainOption | undefined {
    return this.kind === 'choice' ? choiceOption(this.property, this.value, this.choiceDomains) : undefined
  }

  get person(): WorkspacePerson | undefined {
    return this.kind === 'user' ? personOf(this.value, this.people) : undefined
  }

  get variantClass(): string {
    return `pv-${this.variant}`
  }

  get href(): string | null {
    return utils.urlHref(this.value)
  }

  get face() {
    return booleanFace(utils.asBoolean(this.value))
  }

  get relContext(): InstanceContext {
    return {
      system: this.context.system,
      idea: this.property.relationship!.idea,
      instance: this.value._instance
    }
  }

  get relHref(): string {
    return resolveInstanceHref(this.$router, this.relContext)
  }

  /**
   * A cor do item apontado, derivada do id dele.
   *
   * Derivada AQUI, e não recebida por prop: antes a cor vinha de fora — era a da situação do
   * item —, e por isso só existia nas duas telas que se davam ao trabalho de passá-la; o chip da
   * ficha e o do cabeçalho de bloco ficavam sem cor nenhuma. Calculada onde se desenha, ela
   * aparece em toda tela que já desenhava por este componente, e a fiação que a transportava
   * deixa de existir.
   *
   * O `primary` continua como último recurso, para o valor que ainda não tem id.
   */
  get relColor(): string {
    //A cor-BASE, e não a tinta escura: a régua é mancha, e o `ColorAccent` já a escurece para
    //o tom do acento. Escurecer aqui também daria duas voltas na mesma cor.
    return instanceColorOf(this.value) || ''
  }
}

export default toNative(PropertyValue)
</script>

<style scoped>
/* Estas regras viviam duplicadas nos <style> de ListInstances e InstanceView, com
   pequenas diferenças entre as duas telas — preservadas aqui pela variante. */

/* O resumo com o ⤢ (ver MarkdownExpandable) na largura teto da URL na célula */
.pv-markdown-cell {
  max-width: 30em;
}

/* Caixa de tamanho fixo: a imagem nunca a estica, e o espaço já está reservado
   antes de ela chegar — nada salta quando carrega. contain e não cover porque o
   campo tanto guarda foto quanto logotipo ou captura de tela, e recortar o centro
   de um logotipo em 40px produz um retângulo sem sentido. */
.pv-image {
  /* zera o botão: borda e padding do navegador estourariam a caixa de 40px */
  appearance: none;
  border: none;
  padding: 0;
  font: inherit;

  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: 4px;
  background-color: #f5f5f5;
  cursor: zoom-in;
  vertical-align: middle;
}

.pv-image img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  display: block;
}

/* Cresce a linha além dos 48px padrão do Vuetify, de propósito: em 40px a
   miniatura não dava para reconhecer a imagem.
   A altura é fixa para a linha não saltar quando a imagem chega; a largura
   encolhe em volta dela, então não sobra faixa nas laterais, e o teto impede que
   uma imagem panorâmica alargue a coluna. */
.pv-image.pv-cell {
  height: 80px;
  max-width: 140px;
  background-color: transparent;
}

.pv-image.pv-cell img {
  width: auto;
  height: auto;
  max-width: 140px;
  max-height: 80px;
}

.pv-image.pv-detail {
  height: 240px;
  max-width: 100%;
  min-width: 120px;
  background-color: transparent;
}

.pv-image.pv-detail img {
  width: auto;
  max-width: 100%;
}

/* No card a caixa encolhe em volta da imagem, em vez de a imagem ser contida numa
   caixa de altura fixa — é a caixa fixa que produzia faixa cinza nas laterais.
   A imagem cresce até a largura do card e a altura acompanha a proporção; se
   esbarrar no teto de altura, é a largura que cede, então continua sem faixa.
   Sem fundo: não há área vazia que precise de moldura. */
.pv-image.pv-card {
  height: 80px;
  max-width: 100%;
  min-width: 0;
  background-color: transparent;
  cursor: inherit;
}

.pv-image.pv-card img {
  width: auto;
  height: auto;
  max-width: 100%;
  max-height: 80px;
}

/* Toda célula da listagem já é cursor:pointer e abre a instância. Sem um sinal
   próprio, nada diria que aqui o clique faz outra coisa. */
.pv-image-zoom {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.45);
  opacity: 0;
  transition: opacity 0.15s ease;
}

.pv-image:hover .pv-image-zoom,
.pv-image:focus-visible .pv-image-zoom {
  opacity: 1;
}

/* imagem que não carregou: não há o que ampliar */
.pv-image:disabled {
  cursor: default;
}

.url-value {
  overflow-wrap: anywhere;
  word-break: break-word;
}

/* dentro de celulas de tabela, mantem uma linha e corta com reticencias */
.url-value--cell {
  display: inline-block;
  max-width: 30em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  vertical-align: bottom;
}

.pv-audio-mark {
  display: inline-flex;
  align-items: center;
}

.pv-boolean {
  white-space: nowrap;
}

/* Uma pessoa por linha, com um respiro entre elas: no múltiplo, as caras não se encostam */
.pv-person {
  margin-bottom: 2px;
}

.pv-person--unassignable {
  opacity: 0.55;
}

/* Cola o sinal na palavra, sem o espaço que o v-icon deixaria */
.pv-boolean .v-icon {
  margin-right: 2px;
  vertical-align: baseline;
}

/* inline-flex com `stretch`: é o que dá à régua a altura da LINHA do texto, sem número
   nenhum escrito aqui. O respiro à direita separa valores seguidos, que é o papel que a
   borda do chip fazia antes. */
.pv-rel {
  display: inline-flex;
  align-items: stretch;
  gap: 6px;
  max-width: 100%;
  margin: 0 10px 2px 0;
  vertical-align: top;
}

/* 3px aqui, e não os 4px da janela e da linha: é a mesma marca em outra escala — ao lado de
   um nome, 4px viram bloco. Entra por variável para não disputar a largura com o estilo do
   próprio componente, que foi como a régua do cabeçalho já sumiu uma vez. */
.pv-rel-accent {
  --ontos-accent-width: 3px;
}

/* O link não se anuncia por cor nem por sublinhado permanente — a régua já diz que há coisa
   ali. O sublinhado aparece no hover, que é como o resto da web diz "isto abre algo". */
.pv-rel-link {
  color: inherit;
  text-decoration: none;
}

.pv-rel-link:hover .pv-rel-text {
  text-decoration: underline;
}

/* O v-chip já traz `white-space: nowrap` e `overflow: hidden`, mas NÃO traz
   `text-overflow`: um identificador longo era cortado a seco, sem nada indicando que
   continuava. Aqui ele ganha reticências, e o texto inteiro fica no title — o mesmo
   tratamento do cabeçalho do card e do título da faixa.

   min-width: 0 porque o span é item de flex, e item de flex não encolhe abaixo do
   próprio conteúdo sem isso — sem ele não haveria o que cortar. */
.pv-rel-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
